/**
 * Navbar component functionality
 */

import {
    setAriaExpanded,
    setAriaHidden,
    isActivationKey,
    announceToScreenReader,
    setHiddenAndUnfocusable
} from '../Utils/accessibility-utils';

interface INavLink {
    href: string;
    ariaLabel: string;
    text: string;
    withDropdown?: boolean;
    subLinks?: INavLink[];
    newsBlock?: INewsBlock;
}

interface INewsBlock {
    text: string;
    button?: IButton;
}

interface IButton {
    text: string;
    ariaLabel: string;
    href?: string;
    className?: string;
}

interface ILocale {
    code: string;
    label: string;
}

interface ILangSelector {
    locales: ILocale[];
    currentLocale: string;
}

export interface INavbarOptions {
    logo: {
        src: string;
        alt: string;
        href: string;
    };
    navLinks: INavLink[];
    langSelector: ILangSelector;
    searchButton: {
        ariaLabel: string;
        quickLinks?: {
            text: string;
            href: string;
        }[];
    };
    ctaButtons: IButton[];
    className?: string;
}

export class Navbar {
    private supportedLocales = ['en', 'fr', 'de', 'es'];
    private currentLocale: string = '';
    private lastActiveDropdown: HTMLElement | null = null;
    private lastSearchTrigger: HTMLElement | null = null; // NEW: Track search open trigger

    constructor() {
        this.init();
    }

    private init = () => {
        const breakpointDesktopLarge = 1200;
        const navbarWrapper = document.querySelector('.navbar-wrapper') as HTMLElement;
        const navbar = document.querySelector('.navbar') as HTMLElement;
        const mobileToggle = navbar.querySelector('.navbar__mobile-toggle') as HTMLElement;
        const navbarContent = navbar.querySelector('.navbar__content') as HTMLElement;
        const dropdownLinks = navbar.querySelectorAll('.navbar__link-with-dropdown .navbar__link') as NodeListOf<HTMLElement>;
        const nonDropdownLinks = navbar.querySelectorAll('.navbar__links .navbar__link-item:not(.navbar__link-with-dropdown) .navbar__link, .navbar__logo') as NodeListOf<HTMLElement>;
        const languageSelector = navbar.querySelector('.navbar__language-selector') as HTMLElement;
        const languageOptions = navbar.querySelector('.navbar__language-options') as HTMLElement;
        const languageWrapper = navbar.querySelector('.navbar__language-wrapper') as HTMLElement;
        const searchButtons = navbar.querySelectorAll('.navbar__search-btn') as NodeListOf<HTMLElement>;
        const searchOverlay = navbar.querySelector('.navbar__search-overlay') as HTMLElement;
        const searchInput = searchOverlay?.querySelector('input') as HTMLInputElement;
        const skipButton = document.querySelector('.navbar__skip-btn') as HTMLElement;
        const searchResults = searchOverlay?.querySelector('#resultsList') as HTMLElement;
        const searchNoResults = searchOverlay?.querySelector('.no-results') as HTMLElement;
        const searchError = searchOverlay?.querySelector('.search-error') as HTMLElement;
        const searchInvalidInput = searchOverlay?.querySelector('.invalid-input') as HTMLElement;
        let debounceTimer: number | undefined;
        let currentFetchController: AbortController | null = null;
        const currentUrl = window.location.pathname.split('/').filter(Boolean);
        this.currentLocale = this.supportedLocales.includes(currentUrl[0]) ? currentUrl[0] : "en";

        const setInertForHeader = (inert: boolean) => {
            const focusables = navbar.querySelectorAll<HTMLElement>(
                'a, button, input, select, textarea, [tabindex]'
            );
            focusables.forEach(el => {
                if (searchOverlay.contains(el)) return;
                if (inert) {
                    el.setAttribute('data-prev-tab', el.tabIndex.toString());
                    el.setAttribute('tabindex', '-1');
                } else {
                    const prev = el.getAttribute('data-prev-tab');
                    el.removeAttribute('data-prev-tab');
                    if (prev) el.setAttribute('tabindex', prev);
                    else el.removeAttribute('tabindex');
                }
            });
        };

        const trapFocus = (container: HTMLElement) => {
            const getFocusable = () =>
                Array.from(container.querySelectorAll<HTMLElement>(
                    'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
                ));

            container.addEventListener('keydown', e => {
                if (e.key !== 'Tab') return;
                const focusables = getFocusable();
                const first = focusables[0];
                const last = focusables[focusables.length - 1];

                if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                } else if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            });
        };

        const closeNavDropdowns = (): void => {
            dropdownLinks.forEach(l => {
                const parent = l.closest('.navbar__link-with-dropdown') as HTMLElement;
                if (!parent) return;
                parent.classList.remove('active');
                setAriaExpanded(l, false);
                const submenu = parent.querySelector('.navbar__submenu') as HTMLElement;
                setHiddenAndUnfocusable(submenu, true);
            });
            this.lastActiveDropdown = null;
        };

        const skipToMainContent = (event: Event): void => {
            event.preventDefault();
            if (!navbarWrapper) return;

            const nextElement = (navbarWrapper.nextElementSibling ?? document.querySelector('main')) as HTMLElement;
            if (!nextElement) return;

            if (!nextElement.hasAttribute('tabindex')) {
                nextElement.setAttribute('tabindex', '-1');
                nextElement.focus();
                nextElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => nextElement.removeAttribute('tabindex'), 1000);
            } else {
                nextElement.focus();
            }
        };

        const toggleMobileMenu = (): void => {
            const isExpanded = mobileToggle.getAttribute('aria-expanded') === 'true';

            if (isExpanded) {
                navbarContent.classList.remove('active');
                setAriaExpanded(mobileToggle, false);
                setHiddenAndUnfocusable(navbarContent, true);
                document.body.classList.remove('navbar-menu-open');
                closeNavDropdowns();
            } else {
                navbarContent.classList.add('active');
                setAriaExpanded(mobileToggle, true);
                setHiddenAndUnfocusable(navbarContent, false);
                document.body.classList.add('navbar-menu-open');
                closeSearchOverlay();
            }
        };

        const toggleDropdown = (event: Event): void => {
            event.preventDefault();
            event.stopPropagation();

            const link = event.currentTarget as HTMLElement;
            const parent = link.closest('.navbar__link-with-dropdown') as HTMLElement;
            if (!parent) return;

            const submenu = parent.querySelector('.navbar__submenu') as HTMLElement;
            const isMobile = window.innerWidth <= breakpointDesktopLarge;
            const isOpen = parent.classList.contains('active');

            if (isMobile) {
                if (isOpen) {
                    parent.classList.remove('active');
                    setAriaExpanded(link, false);
                    setHiddenAndUnfocusable(submenu, true);
                } else {
                    parent.classList.add('active');
                    setAriaExpanded(link, true);
                    setHiddenAndUnfocusable(submenu, false);
                    parent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else {
                closeNavDropdowns();
                parent.classList.add('active');
                setAriaExpanded(link, true);
                setHiddenAndUnfocusable(submenu, false);
            }

            closeSearchOverlay();
        };

        const toggleDropdownOnEnter = (event: KeyboardEvent): void => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            event.stopPropagation();

            const link = event.currentTarget as HTMLElement;
            const parent = link.closest('.navbar__link-with-dropdown') as HTMLElement;
            const submenu = parent.querySelector('.navbar__submenu') as HTMLElement;

            // Toggle
            if (parent.classList.contains('active')) {
                parent.classList.remove('active');
                setAriaExpanded(link, false);
                setHiddenAndUnfocusable(submenu, true);
            } else {
                closeNavDropdowns();
                parent.classList.add('active');

                setAriaExpanded(link, true);
                setHiddenAndUnfocusable(submenu as HTMLElement, false);
                this.lastActiveDropdown = parent;
            }
        };

        const openDropdownOnMouseover = (event: Event): void => {

            if (window.innerWidth > breakpointDesktopLarge) {
                const link = event.currentTarget as HTMLElement;
                const parent = link.closest('.navbar__link-with-dropdown') as HTMLElement;

                closeNavDropdowns();
                parent.classList.add('active');

                setAriaExpanded(link, true);
                setHiddenAndUnfocusable(parent.querySelector('.navbar__submenu') as HTMLElement, false);
                this.lastActiveDropdown = parent;

                link.focus();
            }
        };

        const handleDropdownBlur = (event: Event): void => {
            if (window.innerWidth > breakpointDesktopLarge) {
                setTimeout(() => {
                    const activeElement = document.activeElement;
                    const link = event.target as HTMLElement;
                    const parent = link.closest('.navbar__link-with-dropdown') as HTMLElement;

                    if (parent && !parent.contains(activeElement) && !parent.classList.contains('active')) {
                        parent.classList.remove('active');
                        setAriaExpanded(parent.querySelector('.navbar__link') as HTMLElement, false);
                        setHiddenAndUnfocusable(parent.querySelector('.navbar__submenu') as HTMLElement, true);
                        if (this.lastActiveDropdown === parent) this.lastActiveDropdown = null;
                    }
                }, 0);
            }
        };

        const toggleLanguageOptions = (event: Event): void => {
            event.stopPropagation();
            if (!languageOptions || !languageWrapper) return;

            const isOpening = !languageOptions.classList.contains('active');

            languageOptions.classList.toggle('active', isOpening);
            languageWrapper.classList.toggle('active', isOpening);
            setAriaExpanded(languageSelector, isOpening);
            setHiddenAndUnfocusable(languageOptions, !isOpening);

            if (isOpening) {
                const firstOption = languageOptions.querySelector('.navbar__language-option') as HTMLElement;
                if (firstOption) setTimeout(() => firstOption.focus(), 10);
            }

            closeSearchOverlay();
        };

        const closeLanguageDropdown = (): void => {
            if (!languageOptions || !languageWrapper) return;
            languageOptions.classList.remove('active');
            languageWrapper.classList.remove('active');
            setAriaExpanded(languageSelector, false);
            setHiddenAndUnfocusable(languageOptions, true);
        };

        const handleLanguageFocus = (): void => {
            closeNavDropdowns();
        };

        const handleLanguageSelectorKeydown = (event: KeyboardEvent): void => {
            if (isActivationKey(event)) {
                event.preventDefault();
                toggleLanguageOptions(event);
            } else if (event.key === 'Escape' && languageOptions.classList.contains('active')) {
                event.preventDefault();
                closeLanguageDropdown();
                languageSelector.focus();
            }
        };

        const handleLanguageBlur = (event: Event): void => {
            setTimeout(() => {
                const activeElement = document.activeElement;
                const languageContainer = navbar.querySelector('.navbar__language') as HTMLElement;
                if (languageContainer && !languageContainer.contains(activeElement)) {
                    closeLanguageDropdown();
                }
            }, 0);
        };

        const selectLanguage = (code: string): void => {
            const langDisplay = languageSelector.querySelector('.navbar__language-current');
            if (langDisplay) langDisplay.textContent = code;

            closeLanguageDropdown();
            languageSelector.focus();
            switchLanguage(code);
        };

        const switchLanguage = (code: string): void => {
            const segments = window.location.pathname.split('/').filter(Boolean);
            const lowercaseCode = code.toLowerCase();

            if (segments.length > 0 && this.supportedLocales.includes(segments[0].toLowerCase())) {
                segments[0] = lowercaseCode;
            } else {
                segments.unshift(lowercaseCode);
            }

            window.location.href = '/' + segments.join('/');
        };

        const updateCurrentLanguageDisplay = (): void => {
            const pathSegments = window.location.pathname.split('/').filter(Boolean);
            const currentLangCode = pathSegments[0]?.toLowerCase();

            const options = languageOptions.querySelectorAll<HTMLElement>('[data-language]');
            const isValidLanguage = Array.from(options).some(
                el => el.getAttribute('data-language')?.toLowerCase() === currentLangCode
            );

            if (isValidLanguage) {
                const langDisplay = languageSelector.querySelector('.navbar__language-current');
                if (langDisplay) langDisplay.textContent = currentLangCode.toUpperCase();
            }
        };

        const toggleSearchOverlay = (event: Event): void => {
            event.stopPropagation();
            if (!searchOverlay) return;

            const isOpen = searchOverlay.classList.contains('active');

            if (!isOpen) {
                this.lastSearchTrigger = event.currentTarget as HTMLElement;

                closeNavDropdowns();
                closeLanguageDropdown();
                if (navbarContent.classList.contains('active')) toggleMobileMenu();

                searchOverlay.classList.add('active');
                searchButtons.forEach(btn => setAriaExpanded(btn, true));
                setHiddenAndUnfocusable(searchOverlay, false);
                setInertForHeader(true);
                trapFocus(searchOverlay);

                if (searchInput) setTimeout(() => searchInput.focus(), 100);
                document.body.classList.add('navbar-search-open');
            } else {
                closeSearchOverlay();
            }
        };

        const closeSearchOverlay = (): void => {
            if (searchOverlay && searchOverlay.classList.contains('active')) {
                searchOverlay.classList.remove('active');
                searchButtons.forEach(btn => setAriaExpanded(btn, false));
                setHiddenAndUnfocusable(searchOverlay, true);
                document.body.classList.remove('navbar-search-open');
                setInertForHeader(false);

                if (this.lastSearchTrigger) {
                    const trigger = this.lastSearchTrigger;
                    setTimeout(() => {
                        if (trigger && document.contains(trigger)) {
                            trigger.focus();
                        }
                    }, 0);
                    this.lastSearchTrigger = null;
                }
            }
        };

        const handleSearchKeydown = (event: KeyboardEvent): void => {
            if (isActivationKey(event)) {
                event.preventDefault();
                toggleSearchOverlay(event);
            } else if (event.key === 'Escape' && searchOverlay.classList.contains('active')) {
                event.preventDefault();
                closeSearchOverlay();
            }
        };

        const handleSearchInputKeydown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeSearchOverlay();
            }
        };

        const handleSearchInput = (): void => {
            window.clearTimeout(debounceTimer);
            const query = searchInput.value.trim();

            if (query.length === 0) {
                if (currentFetchController) currentFetchController.abort();
                searchResults.innerHTML = '';
                return;
            }

            if (!/^[\p{L}\p{N}\s\-.,']{1,50}$/u.test(query)) {
                searchResults.innerHTML = `<li>${searchInvalidInput.innerText}</li>`;
                return;
            }

            searchResults.innerHTML = '<li class="search-result-item"><span class="loader"></span></li>';

            const currentLocalPrefix = this.currentLocale == 'en' ? '' : `/${this.currentLocale}`;

            debounceTimer = window.setTimeout(() => {
                if (currentFetchController) currentFetchController.abort();
                currentFetchController = new AbortController();
                const { signal } = currentFetchController;
                
                fetch(`${currentLocalPrefix}/search?query=${encodeURIComponent(query)}`, {
                    signal,
                    headers: { Accept: 'application/json' }
                })
                    .then(res => {
                        if (!res.ok) throw new Error('Search failed');
                        return res.json();
                    })
                    .then((data: { category: string; title: string; description: string; url: string }[]) => {
                        searchResults.innerHTML = '';

                        if (data.length === 0) {
                            searchResults.innerHTML = `<li class="search-result-item">${searchNoResults.innerText}</li>`;
                            return;
                        }

                        data.forEach(item => {
                            searchResults.innerHTML += `
                                <li class="search-result-item">
                                    <a class="link" href="${item.url}">
                                        <span class="category">${item.category}</span>
                                        ${item.title}
                                        <p class="description">${item.description}</p>
                                    </a>
                                </li>
                            `;
                        });
                    })
                    .catch(err => {
                        if (err.name === 'AbortError') return;
                        console.error('Search error:', err);
                        searchResults.innerHTML = `<li class="search-result-item">${searchError.innerText}</li>`;
                    });
            }, 300);
        };

        const handleSearchClick = (event: Event): void => {
            event.preventDefault();
            toggleSearchOverlay(event);
        };

        const closeDropdowns = (event: Event): void => {
            const target = event.target as HTMLElement;

            if (
                languageSelector &&
                languageOptions &&
                !languageSelector.contains(target) &&
                !languageOptions.contains(target)
            ) {
                closeLanguageDropdown();
            }

            if (window.innerWidth > breakpointDesktopLarge) {
                if (
                    !navbarContent.contains(target) &&
                    !mobileToggle.contains(target)) {
                    closeNavDropdowns();
                }
            }

            if (window.innerWidth <= breakpointDesktopLarge) {
                if (!navbar.contains(target) && navbarContent.classList.contains('active')) {
                    toggleMobileMenu();
                }
            }

            if (
                searchOverlay &&
                searchOverlay.classList.contains('active') &&
                !searchOverlay.contains(target) &&
                !Array.from(searchButtons).some(btn => btn.contains(target))
            ) {
                closeSearchOverlay();
            }
        };

        const closeBtn = searchOverlay.querySelector('.navbar__search-close') as HTMLElement;
        closeBtn.addEventListener('click', closeSearchOverlay);
        closeBtn.addEventListener('keydown', e => {
            if (isActivationKey(e)) closeSearchOverlay();
        });

        mobileToggle.addEventListener('click', toggleMobileMenu);

        // Stop window scrolling up when focusing on nav items
        const nav = document.querySelectorAll('nav, .navbar__skip');
        nav.forEach(link => {
            link.addEventListener('focusin', () => {
                const scrollY = window.scrollY;
                requestAnimationFrame(() => {
                    window.scrollTo({ top: scrollY });
                });
            })
        })

        dropdownLinks.forEach(link => {
            link.addEventListener('click', toggleDropdown);
            link.addEventListener('keydown', toggleDropdownOnEnter);
            link.addEventListener('blur', handleDropdownBlur);
            link.addEventListener('mouseover', openDropdownOnMouseover);
            link.addEventListener('mouseleave', () => { link.blur(); });
        });

        nonDropdownLinks.forEach(link => {
            link.addEventListener('focus', closeNavDropdowns);
        });

        languageSelector.addEventListener('click', toggleLanguageOptions);
        languageSelector.addEventListener('keydown', handleLanguageSelectorKeydown);
        languageSelector.addEventListener('blur', handleLanguageBlur);
        languageSelector.addEventListener('focus', handleLanguageFocus);
        document.addEventListener('click', closeDropdowns);

        searchButtons.forEach(button => {
            button.addEventListener('click', handleSearchClick);
            button.addEventListener('keydown', handleSearchKeydown);
        });

        if (searchInput) {
            searchInput.addEventListener('keydown', handleSearchInputKeydown);
            searchInput.addEventListener('input', handleSearchInput);
        }

        if (skipButton) {
            skipButton.addEventListener('keydown', (e: KeyboardEvent) => {
                if (isActivationKey(e)) {
                    e.preventDefault();
                    skipToMainContent(e);
                }
            });
            skipButton.addEventListener('click', skipToMainContent);
        }

        languageOptions.querySelectorAll('.navbar__language-option').forEach((option, index, list) => {
            option.addEventListener('click', e => {
                e.stopPropagation();
                const code = (e.currentTarget as HTMLElement).getAttribute('data-language');
                if (code) selectLanguage(code);
            });

            option.addEventListener('keydown', e => {
                const key = (e as KeyboardEvent).key;

                if (isActivationKey(e as KeyboardEvent)) {
                    e.preventDefault();
                    const code = (e.currentTarget as HTMLElement).getAttribute('data-language');
                    if (code) selectLanguage(code);
                } else if (key === 'Escape') {
                    e.preventDefault();
                    closeLanguageDropdown();
                    languageSelector.focus();
                } else if (key === 'ArrowDown') {
                    e.preventDefault();
                    const next = (list[index + 1] as HTMLElement) ?? e.currentTarget as HTMLElement;
                    next.focus();
                } else if (key === 'ArrowUp') {
                    e.preventDefault();
                    const prev = (list[index - 1] as HTMLElement) ?? languageSelector;
                    prev.focus();
                }
            });

            option.addEventListener('blur', handleLanguageBlur);
        });

        dropdownLinks.forEach(link => {
            setAriaExpanded(link, false);
        });

        setAriaExpanded(mobileToggle, false);
        searchButtons.forEach(btn => setAriaExpanded(btn, false));
        setHiddenAndUnfocusable(searchOverlay, true);
        setAriaExpanded(languageSelector, false);
        setHiddenAndUnfocusable(languageOptions, true);

        updateCurrentLanguageDisplay();
    };
}

document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.navbar')) {
        new Navbar();
    }
});